import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChefHat, Plus, X, Sparkles, Clock, Users, Zap } from 'lucide-react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment } from '@react-three/drei';
import GlassCard from '../components/UI/GlassCard';
import FloatingElement from '../components/UI/FloatingElement';
import Food3DModel from '../components/3D/Food3DModel';
import { useFoodAnalysis } from '../contexts/FoodAnalysisContext';

const RecipeGenerator: React.FC = () => {
  const [ingredients, setIngredients] = useState<string[]>(['']);
  const [isGenerating, setIsGenerating] = useState(false);
  const { currentRecipe, generateRecipe } = useFoodAnalysis();

  const addIngredient = () => {
    setIngredients([...ingredients, '']);
  };

  const removeIngredient = (index: number) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const updateIngredient = (index: number, value: string) => {
    const newIngredients = [...ingredients];
    newIngredients[index] = value;
    setIngredients(newIngredients);
  };

  const handleGenerateRecipe = async () => {
    const validIngredients = ingredients.filter(ing => ing.trim() !== '');
    if (validIngredients.length === 0) return;

    setIsGenerating(true);
    try {
      await generateRecipe(validIngredients);
    } catch (error) {
      console.error('Recipe generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-400';
      case 'Medium': return 'text-yellow-400';
      case 'Hard': return 'text-red-400';
      default: return 'text-purple-400';
    }
  };

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <FloatingElement delay={0}>
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
              <ChefHat className="w-10 h-10 text-white" />
            </div>
          </FloatingElement>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Smart Recipe <span className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
              Generator
            </span>
          </h1>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">
            Transform your available ingredients into delicious recipes with AI-powered suggestions, 
            3D cooking animations, and personalized nutritional optimization.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Ingredient Input Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <GlassCard className="p-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <Sparkles className="w-6 h-6 text-yellow-400 mr-2" />
                Available Ingredients
              </h2>
              
              <div className="space-y-4 mb-6">
                {ingredients.map((ingredient, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <input
                      type="text"
                      value={ingredient}
                      onChange={(e) => updateIngredient(index, e.target.value)}
                      placeholder="Enter ingredient..."
                      className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    {ingredients.length > 1 && (
                      <button
                        onClick={() => removeIngredient(index)}
                        className="p-2 text-red-400 hover:text-red-300 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={addIngredient}
                  className="flex items-center space-x-2 bg-white/10 border border-white/20 text-white px-4 py-2 rounded-lg hover:bg-white/20 transition-all duration-200"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Ingredient</span>
                </button>
                
                <motion.button
                  onClick={handleGenerateRecipe}
                  disabled={isGenerating || ingredients.every(ing => ing.trim() === '')}
                  className="flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2 rounded-lg font-semibold hover:from-orange-600 hover:to-red-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isGenerating ? (
                    <>
                      <Zap className="w-4 h-4 animate-pulse" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <ChefHat className="w-4 h-4" />
                      <span>Generate Recipe</span>
                    </>
                  )}
                </motion.button>
              </div>
            </GlassCard>

            {/* 3D Food Visualization */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-8"
            >
              <GlassCard className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">3D Ingredient Preview</h3>
                <div className="h-64 rounded-xl overflow-hidden">
                  <Canvas camera={{ position: [0, 0, 5], fov: 75 }}>
                    <ambientLight intensity={0.5} />
                    <pointLight position={[10, 10, 10]} />
                    <Environment preset="sunset" />
                    
                    {ingredients.filter(ing => ing.trim() !== '').map((ingredient, index) => (
                      <Food3DModel
                        key={index}
                        foodType={ingredient}
                        position={[
                          (index % 3 - 1) * 2,
                          Math.floor(index / 3) * 2 - 1,
                          0
                        ]}
                        scale={0.8}
                      />
                    ))}
                    
                    <OrbitControls enableZoom={false} />
                  </Canvas>
                </div>
              </GlassCard>
            </motion.div>
          </motion.div>

          {/* Recipe Display Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {currentRecipe ? (
              <GlassCard className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">{currentRecipe.name}</h2>
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4 text-purple-400" />
                        <span className="text-purple-200">{currentRecipe.cookingTime} min</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Users className="w-4 h-4 text-purple-400" />
                        <span className="text-purple-200">4 servings</span>
                      </div>
                      <span className={`font-semibold ${getDifficultyColor(currentRecipe.difficulty)}`}>
                        {currentRecipe.difficulty}
                      </span>
                    </div>
                  </div>
                  <ChefHat className="w-8 h-8 text-orange-400" />
                </div>

                {/* Ingredients */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-3">Ingredients</h3>
                  <div className="grid grid-cols-1 gap-2">
                    {currentRecipe.ingredients.map((ingredient, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full" />
                        <span className="text-purple-200">{ingredient}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Instructions */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-3">Instructions</h3>
                  <div className="space-y-3">
                    {currentRecipe.instructions.map((instruction, index) => (
                      <div key={index} className="flex space-x-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </div>
                        <p className="text-purple-200 flex-1">{instruction}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Nutrition Summary */}
                <div className="bg-white/5 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-white mb-3">Nutrition per Serving</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-xl font-bold text-white">{currentRecipe.nutrition.calories}</div>
                      <div className="text-purple-300 text-sm">Calories</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-white">{currentRecipe.nutrition.protein}g</div>
                      <div className="text-purple-300 text-sm">Protein</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-white">{currentRecipe.nutrition.carbs}g</div>
                      <div className="text-purple-300 text-sm">Carbs</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-white">{currentRecipe.nutrition.fat}g</div>
                      <div className="text-purple-300 text-sm">Fat</div>
                    </div>
                  </div>
                </div>
              </GlassCard>
            ) : (
              <GlassCard className="p-12 text-center h-full flex items-center justify-center">
                <div>
                  <motion.div
                    animate={{ 
                      rotate: [0, 10, 0, -10, 0],
                      scale: [1, 1.1, 1]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-full flex items-center justify-center border-2 border-orange-500/30"
                  >
                    <ChefHat className="w-12 h-12 text-orange-400" />
                  </motion.div>
                  <h3 className="text-xl font-semibold text-white mb-3">Ready to Cook</h3>
                  <p className="text-purple-200">
                    Add your available ingredients and let our AI chef create the perfect recipe 
                    tailored to your preferences and dietary needs.
                  </p>
                </div>
              </GlassCard>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RecipeGenerator;